import React, { useEffect, useState } from "react";
import axios from "axios";
import HomeTable from "../../components/Table/HomeTable";
import { areAllDefinedAndNotNull, errorHandler } from "../../helpers/helpers";
import styles from "./Home.module.css";
import {
  Autocomplete,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { UseToast } from "../../components/context/ToastContext";
import { getGenericNewTableHeader } from "../../helpers/materialTableHelper";
import { FILTERS_ON_COLUMN, FILTER_VARIANTS } from "../../constants/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import { API } from "../../utility/Api";

export default function Home({ appType }) {
  const initialFilters = [];

  const [refresh, setRefresh] = useState(false);
  const [rowCount, setRowCount] = useState();
  const [tableData, setTableData] = useState();
  const [tableList, setTableList] = useState([]);
  const [isLoading, setIsLoading] = useState();
  const [isRefetching, setIsRefetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const [columnFilters, setColumnFilters] = useState(initialFilters);
  const [selectedRegion, setSelectedRegion] = useState();
  const [selectedApp, setSelectedApp] = useState();
  const [selectedTable, setSelectedTable] = useState("");
  const tableTitle = `${selectedRegion} | ${selectedApp} | ${selectedTable}`;
  const { setMainMessage, appRegions, appNames } = UseToast();
  const [cancelToken, setCancelToken] = useState(null);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 50,
  });

  const handleRegionChange = (event) => setSelectedRegion(event.target.value);

  const handleAppChange = (event) => setSelectedApp(event.target.value);

  const handleTableChange = (event, value) => {
    setSelectedTable(value);
    setPagination({
      pageNumber: 1,
      pageSize: 50,
    });
  };

  const handleApply = () =>
    getRdsTableData(selectedApp, selectedTable, selectedRegion);

  const getFormattedFilters = () => {
    if (columnFilters.length > 0) {
      for (const { id, value } of columnFilters) {
        if (FILTERS_ON_COLUMN.includes(id)) {
          return { fieldName: id, fieldValue: value };
        }
      }
    }
    return { fieldName: null, fieldValue: null };
  };

  const getRdsTableData = (
    selectedApp,
    selectedTable,
    selectedRegion,
    newCancelToken = {}
  ) => {
    setIsLoading(true);
    const getRdsTableResults = async (token) => {
      const { fieldName, fieldValue } = getFormattedFilters();
      const payload = {
        app: selectedApp,
        rdsTable: selectedTable,
        searchByField: fieldName,
        searchValue: fieldValue,
      };
      const endpoint = "getRdsTableResults";
      let queryParams = `page=${pagination.pageNumber}&size=${pagination.pageSize}&region=${selectedRegion}`;
      try {
        const response = await API.post(`${endpoint}?${queryParams}`, payload, {
          headers: {
            Authorization: `JWT ${token}`,
          },
          cancelToken: newCancelToken.token ? newCancelToken.token : "",
        });
        setTableData(response.data);
        setRowCount(response.data.totalCount);
      } catch (error) {
        setIsError(true);
        const data = errorHandler(error);
        setMainMessage({ appearance: "error", text: data.message });
      }
      setIsError(false);
      setIsLoading(false);
    };

    //eslint-disable-next-line
    AP.context.getToken(function (token) {
      getRdsTableResults(token);
    });
  };

  const onPaginationChange = (event, page) =>
    setPagination({
      pageNumber: page,
      pageSize: 50,
    });

  const resetFilters = () => {
    setColumnFilters([]);
    setPagination({
      pageNumber: 1,
      pageSize: 50,
    });
  };

  useEffect(() => {
    const getRDSTablesList = async () => {
      const endpoint = "getRdsTables";
      let queryParams = `appName=${selectedApp}&region=${selectedRegion}`;
      // eslint-disable-next-line no-undef
      await AP.context.getToken(async function (token) {
        try {
          const response = await API.get(`${endpoint}?${queryParams}`, {
            headers: {
              Authorization: `JWT ${token}`,
            },
          });
          setTableList(response.data.rdsTableList);
        } catch (error) {
          setTableList([]);
          setIsError(true);
          const data = errorHandler(error);
          setMainMessage({ appearance: "error", text: data.message });
        }
        setIsError(false);
        setIsRefetching(false);
        setIsLoading(false);
      });
    };

    if (areAllDefinedAndNotNull(selectedRegion, selectedApp)) {
      getRDSTablesList();
    }
  }, [selectedApp]);

  useEffect(() => {
    if (selectedApp && selectedRegion && selectedTable) {
      if (cancelToken) cancelToken.cancel("Canceling previous request");
      const newCancelToken = axios.CancelToken.source();
      setCancelToken(newCancelToken);
      getRdsTableData(
        selectedApp,
        selectedTable,
        selectedRegion,
        newCancelToken
      );
    }
  }, [columnFilters, pagination]);

  const columns = tableData
    ? tableData.columns.map((item) =>
        getGenericNewTableHeader({
          key: item,
          header: item,
          size: 180,
          minSize: 120,
          isPin: true,
          isDragable: true,
          isOrderable: true,
          isResizable: true,
          isHidden: true,
          isSortable: false,
          filterVariant: FILTER_VARIANTS.TEXT,
          isFilter: FILTERS_ON_COLUMN.includes(item) ? true : false,
        })
      )
    : [];

  const rows = tableData
    ? tableData.data.map((rowData) => convertBooleansToStrings(rowData))
    : [];

  function convertBooleansToStrings(obj) {
    for (let key in obj) {
      if (typeof obj[key] === "boolean") {
        obj[key] = obj[key].toString();
      } else if (typeof obj[key] === "object")
        obj[key] = JSON.stringify(obj[key]);
    }
    return obj;
  }
  return (
    <div className={styles.homeContainer}>
      <div className={styles.homeTopForm}>
        <div className={styles.rightFilter}>
          <FormControl fullWidth size="small" sx={{ m: 0 }}>
            <InputLabel
              htmlFor="select-region-input-label"
              sx={{ fontSize: ".875rem", fontWeight: 600 }}
            >
              Select region
            </InputLabel>
            <Select
              labelId="select-region-label-id"
              id="select-region"
              label="select-region"
              value={selectedRegion}
              onChange={handleRegionChange}
              sx={{ fontSize: ".875rem", fontWeight: 600 }}
            >
              {appRegions.map((region) => (
                <MenuItem value={region}>{region}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ m: 0 }}>
            <InputLabel
              htmlFor="select-app-label"
              sx={{ fontSize: ".875rem", fontWeight: 600 }}
            >
              Select app
            </InputLabel>
            <Select
              labelId="select-app"
              id="select-app"
              label="select-app"
              value={selectedApp}
              onChange={handleAppChange}
              sx={{ fontSize: ".875rem", fontWeight: 600 }}
            >
              {appNames.map((name, index) => (
                <MenuItem key={index} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ m: 0 }}>
            <Autocomplete
              labelId="select-table"
              id="select-table"
              label="select-table"
              value={selectedTable}
              sx={{
                display: "inline-block",
                "& input": {
                  fontSize: ".875rem",
                  fontWeight: 600,
                  padding: "0 4px !important",
                },
                "& > div > label": {
                  fontSize: ".875rem",
                  fontWeight: "600",
                  marginTop: "-6px",
                },
                "& > div > label.MuiInputLabel-shrink": {
                  marginTop: "0",
                },
              }}
              onChange={handleTableChange}
              options={tableList}
              renderOption={(props, option, { selected }) => {
                return (
                  <MenuItem
                    {...props}
                    key={option}
                    value={option}
                    sx={{
                      width: "100%",
                    }}
                  >
                    {option}
                  </MenuItem>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select table" />
              )}
            />
          </FormControl>
          {/* <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button> */}
          <LoadingButton
            variant="contained"
            onClick={handleApply}
            disabled={!(selectedApp && selectedRegion && selectedTable)}
            loading={isLoading}
          >
            Apply
          </LoadingButton>
        </div>
      </div>
      <div className={styles.homeTblContainer}>
        {tableData && (
          <HomeTable
            isError={isError}
            appType={appType}
            isLoading={isLoading}
            isRefetching={isRefetching}
            pagination={pagination}
            setPagination={setPagination}
            onPaginationChange={onPaginationChange}
            rowCount={rowCount}
            resetFilters={resetFilters}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            setRefresh={setRefresh}
            tableData={tableData}
            tableTitle={tableTitle}
            columns={columns}
            data={rows}
          />
        )}
      </div>
    </div>
  );
}
